import { Button } from '@ghp/ui'
import { Eraser, Lock } from 'lucide-react'
import { useCallback, useEffect, useRef, type PointerEvent } from 'react'

import { canMeasureSignature, SIGNATURE_LINE_RATIO, signatureBitmapFor } from './signature-bitmap'
import s from './SignaturePad.module.css'

/**
 * Zone de signature — `<canvas>` + événements pointeur, isolée dans son propre composant
 * pour que le reste de l'onglet n'ait à connaître que « on a signé, voici les octets ».
 *
 * Le tracé sort en **PNG** (`canvas.toBlob`) et devient un média de type `signature` comme
 * un autre : il voyage par le même dépôt binaire que les photos (D5), donc il bénéficie du
 * même rejeu idempotent.
 *
 * ⚠️ Le verrou dur de la fiche est ici (M20) : sans les deux mentions de certification, la
 * signature n'est pas recueillable. C'est le **seul** refus de tout l'éditeur — un tiers ne
 * peut pas attester de ce qu'on ne lui a pas énoncé.
 */
export interface SignaturePadProps {
  /** Le verrou de M20, décidé par l'appelant : les deux mentions sont-elles cochées ? */
  disabled?: boolean
  /** Émis au relâchement du tracé, avec le PNG. */
  onSigned?: (signature: Blob) => void
  /**
   * Émis quand l'agent efface le canvas : à l'appelant de retirer aussi la signature déjà
   * rangée. Sans ce rappel, « Effacer » ne viderait que l'écran — la fiche partirait avec
   * un paraphe que l'agent croit supprimé.
   */
  onCleared?: () => void
  /**
   * Le paraphe déjà rangé, repeint à l'ouverture : sans lui, une fiche rechargée montrait une
   * zone vide au-dessus d'une signature datée (#352).
   */
  ink?: Blob
  /**
   * « Valider la signature » : présent tant que le paraphe posé peut encore s'effacer. C'est le
   * geste qui verrouille — le relâcher du stylet ne le peut pas, un paraphe ayant plusieurs traits.
   */
  onSealed?: () => void
  /** Signature verrouillée : le pied porte cette mention à la place des boutons. */
  sealedHint?: string
}

export function SignaturePad({
  disabled,
  onSigned,
  onCleared,
  ink,
  onSealed,
  sealedHint,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const drawn = useRef(false)
  /** Où le stylet s'est posé — le point à peindre si le geste s'achève sans avoir bougé. */
  const origin = useRef({ x: 0, y: 0 })
  /** L'échelle du bitmap en vigueur — ce qui permet de reporter l'encre sans la déformer. */
  const scale = useRef(1)

  /**
   * Poser la taille du bitmap depuis la zone réellement offerte à l'agent : c'est elle qui donne
   * la proportion du PNG.
   *
   * ⚠️ **Par `clientWidth` / `clientHeight`, jamais par `getBoundingClientRect()`.** Le canvas
   * porte une bordure de 1,5 px et le dépôt est en `box-sizing: border-box` : la boîte de
   * bordure fait 900 × 208 quand le bitmap est peint dans 897 × 205. Mesurer la première laissait
   * 1,1 % d'erreur de proportion — l'objet même de cette fiche — que la tolérance des tests ne
   * voyait pas.
   *
   * ⚠️ **L'encre est repeinte plutôt que perdue, et c'est ce qui autorise à mesurer sur un
   * paraphe déjà tracé.** Refuser la mesure (l'ancienne règle) échangeait une perte contre une
   * autre : après une rotation, le PNG archivé gardait la proportion d'avant, et `pointOf()`
   * posait les traits suivants à une autre échelle que les précédents dans la même image.
   *
   * ⚠️ Le court-circuit d'égalité n'est pas une optimisation : écrire `canvas.width` réalloue la
   * mémoire de rendu et efface, **même à valeur identique**. Sans lui, un observateur qui émet à
   * taille inchangée détruirait le paraphe.
   */
  const measure = useCallback(() => {
    const canvas = canvasRef.current
    if (canvas === null) return
    if (!canMeasureSignature({ drawing: drawing.current })) return

    const next = signatureBitmapFor(
      { width: canvas.clientWidth, height: canvas.clientHeight },
      window.devicePixelRatio,
    )

    if (next.width === canvas.width && next.height === canvas.height) return

    const ink = drawn.current ? inkOf(canvas) : null

    // ⚠️ L'encre se reporte à l'**échelle**, jamais « à la nouvelle taille ». La hauteur de la
    // zone est figée par le CSS et sa largeur suit le conteneur : remplir le nouveau bitmap
    // avec l'ancien l'étirait horizontalement à chaque changement de largeur — rotation de la
    // tablette, barre d'URL qui se replie, barre de défilement qui disparaît. Le paraphe sortait
    // déformé, c'est-à-dire le défaut même que cette fiche répare.
    //
    // À densité d'écran constante — le cas courant — le facteur vaut 1 : l'encre reste
    // exactement où l'agent l'a tracée, et le canvas gagne ou perd des pixels sur sa droite.
    const factor = next.scale / scale.current

    canvas.width = next.width
    canvas.height = next.height
    scale.current = next.scale

    if (ink !== null) {
      canvas.getContext('2d')?.drawImage(ink, 0, 0, ink.width * factor, ink.height * factor)
    }
  }, [])

  /**
   * Le signal est la **taille du canvas**, pas celle de la fenêtre.
   *
   * ⚠️ `window.resize` n'en était qu'une approximation, et elle laissait deux trous. Une première
   * mesure à 0 × 0 — ancêtre pas encore visible, restauration bfcache, transition de route qui
   * diffère la disposition — épinglait le canvas sur le repli 640 × 220 **pour toute la vie du
   * composant**, c'est-à-dire #171 lui-même, sans nouvelle tentative. Et tout changement de boîte
   * qui n'est pas un redimensionnement de fenêtre (bascule de `data-density`, apparition d'une
   * barre de défilement, largeur du conteneur) passait inaperçu. Un `ResizeObserver` se déclenche
   * à la première disposition et n'émet que sur changement réel : les deux trous se ferment, et
   * la rafale d'événements d'un glissement de fenêtre disparaît avec.
   */
  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas === null) return undefined

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(canvas)

    return () => {
      observer.disconnect()
    }
  }, [measure])

  /**
   * Repeindre le paraphe rangé, une seule fois : les octets reviennent du stockage à chaque
   * relecture des médias, et repeindre par-dessus l'encre en place l'épaissirait. Peint à sa
   * taille d'origine, comme `measure` reporte l'encre : à densité d'écran constante, le trait
   * revient exactement où il a été posé.
   */
  useEffect(() => {
    if (ink === undefined || drawn.current || typeof createImageBitmap !== 'function') return

    let cancelled = false
    void createImageBitmap(ink).then((bitmap) => {
      if (cancelled || drawn.current || drawing.current) return
      contextOf()?.drawImage(bitmap, 0, 0)
      drawn.current = true
    })

    return () => {
      cancelled = true
    }
  }, [ink])

  function contextOf(): CanvasRenderingContext2D | null {
    // ⚠️ `null` est un cas réel, pas une paranoïa : un canvas non rendu, un contexte déjà
    // pris, ou un environnement sans rendu 2D. On n'y dessine pas, on ne casse pas.
    return canvasRef.current?.getContext('2d') ?? null
  }

  function pointOf(event: PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = event.currentTarget
    const bounds = canvas.getBoundingClientRect()

    // Le bitmap n'a pas la même échelle que la taille CSS du canvas (densité d'écran, plafond
    // de définition) : sans mise à l'échelle, l'encre dévie du stylet. Relu à chaque événement
    // — une rotation de la tablette change le rapport en cours de tracé.
    //
    // ⚠️ L'origine se décale de la **bordure**, et les côtés sont ceux de la boîte de contenu.
    // `getBoundingClientRect()` rend la boîte de bordure : s'en servir telle quelle posait
    // chaque trait 1,5 px CSS sous et à droite du stylet, avec 0,33 % de dérive en largeur —
    // le symptôme même que ce calcul existe pour empêcher.
    const border = { x: borderOf(canvas, 'left'), y: borderOf(canvas, 'top') }
    const scaleX = canvas.clientWidth === 0 ? 1 : canvas.width / canvas.clientWidth
    const scaleY = canvas.clientHeight === 0 ? 1 : canvas.height / canvas.clientHeight

    return {
      x: (event.clientX - bounds.left - border.x) * scaleX,
      y: (event.clientY - bounds.top - border.y) * scaleY,
    }
  }

  function start(event: PointerEvent<HTMLCanvasElement>): void {
    if (disabled === true) return

    const context = contextOf()
    if (context === null) return

    // Capturer le pointeur : un doigt qui sort de la zone en signant ne doit pas couper le
    // trait au milieu d'un paraphe.
    event.currentTarget.setPointerCapture(event.pointerId)
    drawing.current = true

    const { x, y } = pointOf(event)
    origin.current = { x, y }
    // Dérivé du canvas plutôt que mémorisé : c'est lui qui porte la taille en vigueur, et un
    // second dépôt de la même valeur finirait par en diverger après un redimensionnement.
    context.lineWidth = event.currentTarget.width * SIGNATURE_LINE_RATIO
    context.lineCap = 'round'
    context.beginPath()
    context.moveTo(x, y)
  }

  function extend(event: PointerEvent<HTMLCanvasElement>): void {
    if (!drawing.current) return

    const context = contextOf()
    if (context === null) return

    const { x, y } = pointOf(event)
    context.lineTo(x, y)
    context.stroke()
    drawn.current = true
  }

  /**
   * Le stylet se relève. **Un appui sans le moindre mouvement signe** : une croix nette, un
   * point, un tap — le prototype signe au clic, et l'agent qui n'a rien vu apparaître ne peut
   * pas distinguer son geste du verrou des mentions.
   *
   * ⚠️ Le point n'est peint qu'**ici**, pas à l'appui, et c'est ce qui le sépare d'un contact
   * involontaire : un geste que le navigateur reprend pour défiler s'achève en
   * `pointercancel` ({@link cancel}), qui ne peint rien. Faire naître l'encre sous le doigt
   * ferait signer un tiers sur un frôlement.
   */
  function end(): void {
    if (!drawing.current) return
    if (!drawn.current) mark()

    finish()
  }

  /**
   * Le navigateur a repris le geste — défilement, zoom, stylet sorti de l'écran. Ce qui est
   * **déjà tracé** reste et part : l'encre est sur le canvas, et la perdre coûterait un paraphe
   * que personne ne refait. Ce qui ne naît pas, c'est le point d'un appui qu'on n'a pas voulu.
   */
  function cancel(): void {
    if (!drawing.current) return

    finish()
  }

  /** Le point d'appui, au diamètre du trait : l'encre d'un geste qui n'a pas bougé. */
  function mark(): void {
    const context = contextOf()
    if (context === null) return

    const { x, y } = origin.current
    // Un cercle plein plutôt qu'un segment de longueur nulle : le rendu de celui-ci dépend du
    // moteur, et un paraphe qui n'apparaît que sur certaines tablettes est le défaut qu'on
    // répare ici.
    context.beginPath()
    context.arc(x, y, context.lineWidth / 2, 0, Math.PI * 2)
    context.fill()
    drawn.current = true
  }

  function finish(): void {
    drawing.current = false

    const canvas = canvasRef.current
    if (canvas === null || !drawn.current) return

    // `toBlob` est asynchrone et peut rendre `null` : on ne remonte que de vrais octets.
    canvas.toBlob((blob) => {
      if (blob !== null) onSigned?.(blob)
    }, 'image/png')
  }

  function clear(): void {
    const context = contextOf()
    if (context === null || canvasRef.current === null) return

    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    drawn.current = false

    // ⚠️ Aucune mesure ici, et c'est délibéré. `onCleared` fait tomber `draft.signedAt` chez le
    // parent, ce qui démonte un bloc de `SubmitTab` : mesurer avant aurait lu la mise en page
    // d'avant ce démontage. L'observateur de taille, lui, voit la boîte d'après — et il la voit
    // aussi quand la disparition d'une barre de défilement élargit le canvas sans qu'aucun
    // événement de fenêtre ne soit émis.
    onCleared?.()
  }

  return (
    <div className={s.root} data-disabled={disabled === true ? '' : undefined}>
      <canvas
        ref={canvasRef}
        className={s.canvas}
        // Le canvas n'a pas de contenu accessible : c'est le libellé qui le nomme. L'état
        // verrouillé, lui, est porté par le conteneur (`data-disabled`) et énoncé en toutes
        // lettres par la note de blocage au-dessus — `role="img"` n'admet pas
        // `aria-disabled`, et une pastille muette ne dirait de toute façon pas pourquoi.
        role="img"
        aria-label="Zone de signature"
        onPointerDown={start}
        onPointerMove={extend}
        onPointerUp={end}
        onPointerCancel={cancel}
      />
      <div className={s.footer}>
        {/* Verbatim du prototype validé : c'est la phrase que l'agent lit au client. */}
        <span className={s.hint}>{sealedHint ?? 'Signez ci-dessus avec le stylet'}</span>
        {sealedHint === undefined && (
          <div className={s.actions}>
            <Button tone="ghost" size="sm" disabled={disabled} onClick={clear}>
              <Eraser size={18} aria-hidden /> Effacer
            </Button>
            {onSealed !== undefined && (
              <Button tone="brand" size="sm" disabled={disabled} onClick={onSealed}>
                <Lock size={18} aria-hidden /> Valider la signature
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Une copie hors écran de ce qui est peint : le canvas est effacé par sa propre
 * redimension, il faut donc tenir l'image avant d'y toucher.
 */
function inkOf(canvas: HTMLCanvasElement): HTMLCanvasElement | null {
  const copy = document.createElement('canvas')
  copy.width = canvas.width
  copy.height = canvas.height

  const context = copy.getContext('2d')
  if (context === null) return null

  context.drawImage(canvas, 0, 0)

  return copy
}

/** L'épaisseur d'un côté de la bordure, en pixels CSS — 0 si la valeur calculée est illisible. */
function borderOf(canvas: HTMLCanvasElement, side: 'left' | 'top'): number {
  const width = Number.parseFloat(
    window.getComputedStyle(canvas).getPropertyValue(`border-${side}-width`),
  )

  return Number.isFinite(width) ? width : 0
}
